import { addUser } from "../store/userSlice";

// Keep in sync with ThemeModeContext.jsx and the pre-paint script in index.html
const THEME_STORAGE_KEY = "hajex-theme-mode";

export const SHIPPER_ROLES = ["Shipper", "ShipperUser", "oneTimeShipper"];

export const isShipperRole = (role) => SHIPPER_ROLES.includes(role);

// Backend doesn't always populate mergedPermissions (e.g. shipper helpers).
// Derive it from roles[].permissions[] + direct permissions[] as a fallback.
export const buildUserFromLoginResult = (result) => {
  const rawUser = result?.user ?? {};
  const derivedMerged = Array.from(
    new Set([
      ...(rawUser.permissions ?? []).map((p) => p?.name).filter(Boolean),
      ...(rawUser.roles ?? []).flatMap((r) =>
        (r?.permissions ?? []).map((p) => p?.name).filter(Boolean)
      ),
    ])
  );
  return {
    ...rawUser,
    mergedPermissions: Array.isArray(rawUser.mergedPermissions)
      ? rawUser.mergedPermissions
      : derivedMerged,
    token: result?.token,
  };
};

// `result` is res.data.result from POST /login.
//
// KEEP IN SYNC: this is a deliberate copy of the session-writing block in
// src/Pages/Authentication/LoginForm.jsx (search for "KEEP IN SYNC" there).
// It is duplicated rather than shared so that adding the OAuth flow required
// no edit to that production login path. The keys, their values and their
// ORDER must match it exactly; verified equivalent for Shipper, ShipperUser,
// oneTimeShipper, Carrier and warehouseUser results. If LoginForm gains a key,
// add it here too.
//
// Returns the composed user. Does NOT navigate -- callers own that, which is
// what lets the OAuth consent page use this without leaving /authorize.
export const persistSession = (result, dispatch) => {
  const user = buildUserFromLoginResult(result);
  localStorage.setItem("user", JSON.stringify(user));
  if (dispatch) dispatch(addUser(user));
  localStorage.setItem("Token", result?.token);
  localStorage.setItem("Id", result?.user?.id);
  localStorage.setItem("Name", result?.user?.username);
  localStorage.setItem("Email", result?.user?.email);
  localStorage.setItem("Role", result?.user?.role);
  localStorage.setItem("firstname", result?.user?.firstname);
  localStorage.setItem("lastname", result?.user?.lastname);
  localStorage.setItem("profile_image", result?.user?.profile_image);
  localStorage.setItem("Balance", result?.user?.balance);
  if (isShipperRole(result?.user?.role)) {
    localStorage.setItem("Carriers", JSON.stringify(result?.carrier));
  }
  return user;
};

// Local sign-out. Preserves the theme preference across localStorage.clear(),
// because the switch-account path keeps the SPA alive on the same page instead
// of reloading -- a wiped theme key would silently flip the user's theme
// mid-flow.
//
// addUser({}) and not addUser(null): axiosInstance dereferences
// store.getState().user.token on every request, so null throws a TypeError on
// every subsequent call in the app.
export const clearSession = (dispatch) => {
  let theme = null;
  try {
    theme = localStorage.getItem(THEME_STORAGE_KEY);
  } catch (e) {
    theme = null;
  }
  try {
    localStorage.clear();
  } catch (e) {
    /* storage unavailable (private mode) -- nothing to clear */
  }
  try {
    if (theme) localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch (e) {
    /* non-fatal: the user keeps the system theme */
  }
  if (dispatch) dispatch(addUser({}));
};

// Single source of truth for "who is signed in", reading localStorage exactly
// like the route guards do, so a page can never disagree with them.
export const readSession = () => {
  let user = {};
  try {
    user = JSON.parse(localStorage.getItem("user")) || {};
  } catch (e) {
    user = {};
  }
  return {
    token: localStorage.getItem("Token") || null,
    role: localStorage.getItem("Role") || null,
    user,
  };
};
